import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { Routes } from '../../routes.ts'
import {
  isBeaconRunUnfinished,
  useGetRepositoryLatestBeaconRun,
  usePostBeaconScan,
} from '../../utils/api-client/api-hooks.ts'
import type { BeaconEffort } from '../../utils/api-client/api-schemas.ts'
import {
  SplitButton,
  type SplitButtonItem,
  type SplitButtonSize,
  type SplitButtonType,
} from '../buttons/split-button.tsx'

/** Effort is the token budget, nothing more; the copy says what a bigger or smaller one buys. */
export const BEACON_EFFORT_ITEMS: readonly SplitButtonItem<BeaconEffort>[] = [
  { value: 'low', label: 'Low', description: 'Smaller budget, stops sooner' },
  { value: 'medium', label: 'Medium', description: 'The standard budget' },
  { value: 'high', label: 'High', description: 'Largest budget, follows more paths, slowest' },
]

export const DEFAULT_BEACON_EFFORT: BeaconEffort = 'medium'

const RUNNING_REASON = 'A Beacon scan is already running on this repository.'

export type BeaconScanButtonPureProps = {
  onStart: (effort: BeaconEffort) => unknown
  onOpenRun: (runId: string) => unknown
  isStarting: boolean
  /** The repository's unfinished run, if it has one; a repository runs one scan at a time. */
  runningRunId?: string
  buttonType?: SplitButtonType
  size?: SplitButtonSize
}

export const BeaconScanButtonPure: React.FC<BeaconScanButtonPureProps> = ({
  onStart,
  onOpenRun,
  isStarting,
  runningRunId,
  buttonType,
  size = 'default',
}) => (
  <SplitButton
    onClick={() => (runningRunId ? onOpenRun(runningRunId) : onStart(DEFAULT_BEACON_EFFORT))}
    menuLabel="Scan effort"
    menuTriggerLabel="Choose scan effort"
    items={BEACON_EFFORT_ITEMS}
    defaultValue={DEFAULT_BEACON_EFFORT}
    onSelect={onStart}
    state={isStarting ? 'loading' : 'default'}
    menuDisabledReason={runningRunId && RUNNING_REASON}
    buttonType={buttonType}
    size={size}
    loadingLabel="Starting Beacon scan"
  >
    {runningRunId ? 'View running scan' : 'Scan with Beacon'}
  </SplitButton>
)

export type BeaconStartButtonProps = Pick<BeaconScanButtonPureProps, 'buttonType' | 'size' | 'runningRunId'> & {
  repositoryId: string
}

/**
 * Starts a Beacon run on a repository, medium effort on the main half and any effort from the menu, then
 * opens the run's page. The platform answers with the repository's unfinished run when it already has one,
 * and that run opens the same way. While a run is in flight the main half opens it and the menu is off.
 * For callers that already know whether a run is unfinished.
 */
export const BeaconStartButton: React.FC<BeaconStartButtonProps> = ({
  repositoryId,
  runningRunId,
  buttonType,
  size,
}) => {
  const navigate = useNavigate()
  const showError = useShowMutationError<typeof usePostBeaconScan>({ prefix: 'Unable to start the Beacon scan' })
  const { mutate, isPending } = usePostBeaconScan({
    onSuccess: run => navigate(Routes.BeaconRunPage.createPath({ runId: run.id })),
    onError: showError,
  })
  const onStart = useCallback((effort: BeaconEffort) => mutate({ repositoryId, effort }), [mutate, repositoryId])
  const onOpenRun = useCallback((runId: string) => navigate(Routes.BeaconRunPage.createPath({ runId })), [navigate])

  return (
    <BeaconScanButtonPure
      onStart={onStart}
      onOpenRun={onOpenRun}
      isStarting={isPending}
      runningRunId={runningRunId}
      buttonType={buttonType}
      size={size}
    />
  )
}

export type BeaconScanButtonProps = Pick<BeaconScanButtonPureProps, 'buttonType' | 'size'> & { repositoryId: string }

/** {@link BeaconStartButton} for a repository, which looks up whether it has a run unfinished. */
export const BeaconScanButton: React.FC<BeaconScanButtonProps> = ({ repositoryId, buttonType, size }) => {
  const { data: latest } = useGetRepositoryLatestBeaconRun({ repositoryId })
  return (
    <BeaconStartButton
      repositoryId={repositoryId}
      runningRunId={isBeaconRunUnfinished(latest) ? latest?.id : undefined}
      buttonType={buttonType}
      size={size}
    />
  )
}
